import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Play, Pause, Square, Volume2, Music, Clock, GripVertical, ListOrdered, Plus, X, ArrowLeft, Settings, Upload, Trash2, Award, Users, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FullscreenButton from '@/components/FullscreenButton';
import Wordsearch from '@/components/Wordsearch';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Announcement file URLs
const getAnnouncementUrl = (filename) => `${BACKEND_URL}/api/audio/announcement/${filename}`;

const ANNOUNCEMENTS = {
  apparatus: {
    120: getAnnouncementUrl('twominutes_apparatus.mp3'),
    60: getAnnouncementUrl('oneminute_apparatus.mp3'),
    0: getAnnouncementUrl('warmupdone.mp3')
  },
  general: {
    120: getAnnouncementUrl('twominute_general.mp3'),
    60: getAnnouncementUrl('oneminute_general.mp3'),
    0: getAnnouncementUrl('generalwarmupdone.mp3')
  }
};

// Preload all announcements to avoid network latency during playback
const preloadedAudio = {};
const preloadAnnouncements = () => {
  Object.values(ANNOUNCEMENTS).forEach(modeAnnouncements => {
    Object.values(modeAnnouncements).forEach(url => {
      if (!preloadedAudio[url]) {
        const audio = new Audio();
        audio.preload = 'auto';
        audio.src = url;
        preloadedAudio[url] = audio;
        console.log(`[Announcements] Preloading: ${url}`);
      }
    });
  });
};

export default function FloorMusicPage() {
  const navigate = useNavigate();
  const [pinEntered, setPinEntered] = useState(false);
  const [pin, setPin] = useState('');
  
  // Competition & Selection
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [programmes, setProgrammes] = useState([]);
  const [selectedRound, setSelectedRound] = useState('');
  const [selectedRotation, setSelectedRotation] = useState('');
  const [rotationSchedule, setRotationSchedule] = useState(null);
  const [floorGroups, setFloorGroups] = useState([]);
  const [gymnasts, setGymnasts] = useState([]);
  const [rotationStarted, setRotationStarted] = useState(false);
  const [loadingGymnasts, setLoadingGymnasts] = useState(false);
  const [playedGymnastIds, setPlayedGymnastIds] = useState(new Set());

  // Webcam
  const webcamVideoRef = useRef(null); // reserved for future webcam feature
  
  // Page title
  usePageTitle('Floor Music');
  
  // Timer preview
  const [timerPreview, setTimerPreview] = useState(null);
  const [previewTime, setPreviewTime] = useState(0);
  const [nowPlayingPreview, setNowPlayingPreview] = useState(null);
  
  // Queue Management
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [draggingIndex, setDraggingIndex] = useState(null);
  
  // Floor Music
  const [floorVolume, setFloorVolume] = useState(70);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Track if music is paused (vs stopped)
  const [pausedGymnastId, setPausedGymnastId] = useState(null); // Track which gymnast's music is paused
  const [playingType, setPlayingType] = useState(null); // 'floor' or 'ceremony'
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Wordsearch entertainment during floor music playback
  const [wordsearchWords, setWordsearchWords] = useState([]);
  const [wordsearchEnabled, setWordsearchEnabled] = useState(true);
  const [showWordsearch, setShowWordsearch] = useState(false);
  
  // Timer
  const [timerMode, setTimerMode] = useState('apparatus');
  const [timerDuration, setTimerDuration] = useState(120);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  
  // Ceremony Music (March/Podium)
  const [ceremonyTracks, setCeremonyTracks] = useState([]);
  const [showCeremonySettings, setShowCeremonySettings] = useState(false);
  const [uploadingCeremony, setUploadingCeremony] = useState(false);
  const [newTrackName, setNewTrackName] = useState('');
  const [newTrackType, setNewTrackType] = useState('march');
  const ceremonyFileRef = useRef(null);
  
  // Audio
  const floorAudioRef = useRef(null);
  const announcementAudioRef = useRef(null);
  const ceremonyAudioRef = useRef(null);
  const announcementQueue = useRef([]);
  const [isAnnouncementPlaying, setIsAnnouncementPlaying] = useState(false);
  const announcementsPlayed = useRef(new Set());
  const hasLoadedInitially = useRef(false);

  // Training Video State
  const [trainingVideoSettings, setTrainingVideoSettings] = useState(null);
  const [trainingVideoPlaying, setTrainingVideoPlaying] = useState(false);

  // Global competition ID for background music coordination
  const backgroundCompetitionId = 'global-background';

  // Preload announcements on component mount
  useEffect(() => {
    preloadAnnouncements();
  }, []);

  // Load wordsearch words
  useEffect(() => {
    const loadWordsearchWords = async () => {
      try {
        const res = await axios.get(`${API}/public/wordsearch-words`);
        setWordsearchEnabled(res.data.enabled !== false);
        if (res.data.words) {
          const words = res.data.words.split(',').map(w => w.trim()).filter(w => w.length >= 3);
          setWordsearchWords(words);
        }
      } catch (e) {
        console.log('No wordsearch words configured');
      }
    };
    loadWordsearchWords();
  }, []);

  // Initialize localStorage
  useEffect(() => {
    const saved = localStorage.getItem('floorMusicSettings');
    if (saved) {
      try {
        const settings = JSON.parse(saved);
        setFloorVolume(settings.floorVolume || 70);
        setSelectedCompetition(settings.selectedCompetition || '');
        setSelectedRound(settings.selectedRound || '');
        setSelectedRotation(settings.selectedRotation || '');
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    }
  }, []);

  // Save to localStorage
  const saveSettings = () => {
    const settings = {
      floorVolume,
      selectedCompetition,
      selectedRound,
      selectedRotation
    };
    localStorage.setItem('floorMusicSettings', JSON.stringify(settings));
  };

  useEffect(() => {
    saveSettings();
  }, [floorVolume, selectedCompetition, selectedRound, selectedRotation]);

  // Cleanup now playing on unmount
  useEffect(() => {
    return () => {
      if (selectedCompetition) {
        axios.post(`${API}/now-playing/stop`, { competitionId: selectedCompetition })
          .catch(err => console.error('Cleanup error:', err));
        axios.put(`${API}/floor-state`, { competitionId: selectedCompetition, isActive: false })
          .catch(() => {});
      }
    };
  }, [selectedCompetition]);

  // PIN submission
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pin === '2507') {
      setPinEntered(true);
      toast.success('Floor Music Control Active');
      loadCompetitions();
      loadCeremonyTracks();
    } else {
      toast.error('Invalid PIN');
      setPin('');
    }
  };

  // Load competitions
  const loadCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/music/competitions`);
      setCompetitions(response.data.competitions || []);
    } catch (error) {
      console.error('Error loading competitions:', error);
      toast.error('Failed to load competitions');
    }
  };

  // Load training video settings when competition changes
  const loadTrainingVideoSettings = async (compId) => {
    if (!compId) {
      setTrainingVideoSettings(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/training-video/settings/${compId}`);
      setTrainingVideoSettings(response.data);
      setTrainingVideoPlaying(response.data.isPlaying || false);
    } catch (error) {
      console.error('Error loading training video settings:', error);
      setTrainingVideoSettings(null);
    }
  };

  // Broadcast training video to all judge tablets
  const broadcastTrainingVideo = async () => {
    if (!selectedCompetition) {
      toast.error('Please select a competition first');
      return;
    }
    if (!trainingVideoSettings?.videoUrl) {
      toast.error('No training video configured for this competition');
      return;
    }
    
    try {
      await axios.post(`${API}/training-video/broadcast/${selectedCompetition}`);
      toast.success('Training video is now playing on all judge tablets!');
      setTrainingVideoPlaying(true);
      setTrainingVideoSettings({ ...trainingVideoSettings, isPlaying: true });
    } catch (error) {
      console.error('Error broadcasting training video:', error);
      toast.error('Failed to broadcast training video');
    }
  };

  // Stop training video broadcast
  const stopTrainingVideo = async () => {
    if (!selectedCompetition) return;
    
    try {
      await axios.post(`${API}/training-video/stop/${selectedCompetition}`);
      toast.success('Training video stopped');
      setTrainingVideoPlaying(false);
      setTrainingVideoSettings({ ...trainingVideoSettings, isPlaying: false });
    } catch (error) {
      console.error('Error stopping training video:', error);
      toast.error('Failed to stop training video');
    }
  };

  // Load ceremony music tracks
  const loadCeremonyTracks = async () => {
    try {
      const response = await axios.get(`${API}/ceremony-music`);
      setCeremonyTracks(response.data.tracks || []);
    } catch (error) {
      console.error('Error loading ceremony tracks:', error);
    }
  };

  // Upload ceremony music
  const uploadCeremonyTrack = async () => {
    const file = ceremonyFileRef.current?.files?.[0];
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    if (!newTrackName.trim()) {
      toast.error('Please enter a track name');
      return;
    }

    setUploadingCeremony(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', newTrackName.trim());
      formData.append('music_type', newTrackType);

      await axios.post(`${API}/ceremony-music/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Uploaded ${newTrackName}`);
      setNewTrackName('');
      ceremonyFileRef.current.value = '';
      loadCeremonyTracks();
    } catch (error) {
      console.error('Error uploading ceremony track:', error);
      toast.error('Failed to upload track');
    } finally {
      setUploadingCeremony(false);
    }
  };

  // Delete ceremony music
  const deleteCeremonyTrack = async (trackId, trackName) => {
    if (!confirm(`Delete "${trackName}"?`)) return;
    
    try {
      await axios.delete(`${API}/ceremony-music/${trackId}`);
      toast.success('Track deleted');
      loadCeremonyTracks();
    } catch (error) {
      console.error('Error deleting ceremony track:', error);
      toast.error('Failed to delete track');
    }
  };

  // Play ceremony music (march/podium)
  const playCeremonyMusic = (track) => {
    if (!ceremonyAudioRef.current) {
      toast.error('Audio player not ready');
      return;
    }

    // Signal background music to pause
    signalBackgroundPause('floor_music');

    setTimeout(() => {
      const audioUrl = `${API}/audio/ceremony/${track.id}`;
      ceremonyAudioRef.current.src = audioUrl;
      ceremonyAudioRef.current.volume = floorVolume / 100;
      
      ceremonyAudioRef.current.play()
        .then(() => {
          setCurrentTrack(track.name);
          setIsPlaying(true);
          setPlayingType('ceremony');
          toast.success(`Playing: ${track.name}`);
        })
        .catch(error => {
          console.error('Error playing ceremony music:', error);
          toast.error('Failed to play track');
          signalBackgroundResume();
        });
    }, 400);
  };

  const handleCeremonyMusicEnded = async () => {
    setIsPlaying(false);
    setCurrentTrack(null);
    setPlayingType(null);
    await signalBackgroundResume();
  };

  // Stop ceremony music
  const stopCeremonyMusic = async () => {
    if (ceremonyAudioRef.current) {
      ceremonyAudioRef.current.pause();
      ceremonyAudioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTrack(null);
    setPlayingType(null);
    await signalBackgroundResume();
  };

  // Load programme when competition selected
  useEffect(() => {
    if (selectedCompetition) {
      loadProgramme();
    }
  }, [selectedCompetition]);

  const loadProgramme = async () => {
    try {
      const response = await axios.get(`${API}/music/programme/${selectedCompetition}`);
      const prog = response.data.programme;
      if (prog) {
        setProgrammes(prog.rounds || []);
      }
    } catch (error) {
      console.error('Error loading programme:', error);
      toast.error('Failed to load programme');
    }
  };

  // Poll timer preview and now playing every 500ms
  useEffect(() => {
    if (!selectedCompetition || !pinEntered) return;

    let isPolling = true; // Track if we should continue polling

    const pollTimerPreview = async () => {
      if (!isPolling) return;
      
      try {
        const [timerResponse, nowPlayingResponse] = await Promise.all([
          axios.get(`${API}/timer/state/${selectedCompetition}`),
          axios.get(`${API}/now-playing/${selectedCompetition}`)
        ]);
        
        const timerData = timerResponse.data.timer;
        const nowPlayingData = nowPlayingResponse.data.nowPlaying;
        
        setNowPlayingPreview(nowPlayingData);
        
        if (timerData && timerData.isRunning) {
          setTimerPreview(timerData);
          
          // Use server-calculated remaining time to avoid drift
          const remaining = timerData.calculatedRemaining ?? timerData.remainingTime ?? 0;
          setPreviewTime(remaining);

          // Trigger announcements based on server time
          if (!timerData.isPaused && hasLoadedInitially.current) {
            const announcementTimes = [
              { seconds: 120, label: '2:00' },
              { seconds: 60, label: '1:00' },
              { seconds: 0, label: '0:00' }
            ];
            
            announcementTimes.forEach(({ seconds, label }) => {
              const key = `${timerData.id}-${seconds}`;
              
              if (!announcementsPlayed.current.has(key)) {
                let shouldTrigger = false;
                
                if (seconds === 0) {
                  // Warmup done - trigger when remaining is 0 or less (in case we miss exact 0)
                  shouldTrigger = remaining <= 0;
                } else {
                  // For 120s and 60s, trigger within a 3-second window to not miss it
                  shouldTrigger = remaining <= seconds && remaining > (seconds - 3);
                }
                
                if (shouldTrigger) {
                  console.log(`[Announcement] Triggering for ${seconds}s (remaining: ${remaining}, mode: ${timerData.mode})`);
                  announcementsPlayed.current.add(key);
                  queueAnnouncement(seconds, timerData.mode);
                }
              }
            });
          }
          
          if (!hasLoadedInitially.current) {
            hasLoadedInitially.current = true;
          }
        } else {
          setTimerPreview(null);
          setPreviewTime(0);
          announcementsPlayed.current.clear();
        }
      } catch (error) {
        console.error('Error polling timer preview:', error);
      }
    };

    pollTimerPreview();
    const interval = setInterval(pollTimerPreview, 500);

    return () => {
      isPolling = false; // Stop polling when unmounting
      clearInterval(interval);
    };
  }, [selectedCompetition, pinEntered]);

  // Load rotation schedule when round changes
  useEffect(() => {
    const loadRotationSchedule = async () => {
      if (!selectedCompetition || !selectedRound) {
        setRotationSchedule(null);
        setFloorGroups([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API}/rotation-schedule/${selectedCompetition}/${selectedRound}`
        );
        setRotationSchedule(response.data);
      } catch (error) {
        console.error('Error loading rotation schedule:', error);
        setRotationSchedule(null);
      }
    };

    loadRotationSchedule();
  }, [selectedCompetition, selectedRound]);

  // Find groups on floor when rotation is selected
  useEffect(() => {
    if (!rotationSchedule || !selectedRotation) {
      setFloorGroups([]);
      return;
    }

    const rotNum = parseInt(selectedRotation);
    const groupsOnFloor = [];

    (rotationSchedule.groups || []).forEach(group => {
      const rotation = (group.rotations || []).find(r => r.rotationNumber === rotNum);
      if (rotation && ['floor', 'wagfloor', 'magfloor'].includes(rotation.apparatus)) {
        groupsOnFloor.push(group.groupName);
      }
    });

    setFloorGroups(groupsOnFloor);

    if (groupsOnFloor.length === 0) {
      toast.info(`No groups on Floor for Rotation ${rotNum}`);
    } else {
      toast.success(`Groups on Floor: ${groupsOnFloor.join(', ')}`);
    }
  }, [rotationSchedule, selectedRotation]);

  // Auto-load gymnasts when floor groups are determined
  useEffect(() => {
    if (floorGroups.length > 0 && selectedCompetition && selectedRound && !rotationStarted) {
      loadGymnastsForFloor();
    }
  }, [floorGroups]);

  const loadGymnastsForFloor = async () => {
    if (floorGroups.length === 0) return;
    
    try {
      setLoadingGymnasts(true);
      const response = await axios.get(
        `${API}/music/gymnasts/${selectedCompetition}?round_number=${selectedRound}&group_names=${floorGroups.join(',')}`
      );
      const loadedGymnasts = response.data.gymnasts || [];
      setGymnasts(loadedGymnasts);
      setRotationStarted(true);
      
      // Sync floor state for /open dashboard
      const compTitle = competitions.find(c => c.id === selectedCompetition)?.title || '';
      axios.put(`${API}/floor-state`, {
        competitionId: selectedCompetition,
        competitionTitle: compTitle,
        roundNumber: parseInt(selectedRound),
        rotationNumber: parseInt(selectedRotation),
        isActive: true
      }).catch(() => {});
      
      if (loadedGymnasts.length === 0) {
        toast.warning('No gymnasts with floor music found');
      } else {
        toast.success(`Loaded ${loadedGymnasts.length} gymnast(s) with floor music`);
        
        try {
          const queueResponse = await axios.get(`${API}/music-queue/${selectedCompetition}`);
          if (queueResponse.data.queue?.length > 0) {
            setQueue(queueResponse.data.queue);
            setQueueIndex(queueResponse.data.currentIndex || 0);
          }
        } catch (err) {
          console.error('Error loading queue:', err);
        }
      }
    } catch (error) {
      console.error('Error loading gymnasts:', error);
      toast.error('Failed to load gymnasts');
    } finally {
      setLoadingGymnasts(false);
    }
  };

  // Mark a gymnast as having played — greys out their tile
  const markPlayed = (gymnastId) => {
    if (gymnastId) setPlayedGymnastIds(prev => new Set([...prev, gymnastId]));
  };

  // Save queue to backend
  const saveQueue = async (newQueue, newIndex = queueIndex) => {
    try {
      await axios.put(`${API}/music-queue/${selectedCompetition}`, {
        gymnasts: newQueue,
        currentIndex: newIndex
      });
    } catch (error) {
      console.error('Error saving queue:', error);
    }
  };

  // Move gymnast in queue (drag and drop)
  const moveInQueue = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    const newQueue = [...queue];
    const [moved] = newQueue.splice(fromIndex, 1);
    newQueue.splice(toIndex, 0, moved);
    
    setQueue(newQueue);
    
    let newIndex = queueIndex;
    if (fromIndex === queueIndex) {
      newIndex = toIndex;
    } else if (fromIndex < queueIndex && toIndex >= queueIndex) {
      newIndex = queueIndex - 1;
    } else if (fromIndex > queueIndex && toIndex <= queueIndex) {
      newIndex = queueIndex + 1;
    }
    
    setQueueIndex(newIndex);
    saveQueue(newQueue, newIndex);
  };

  const handleDragStart = (e, index) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    if (draggingIndex !== null && draggingIndex !== toIndex) {
      moveInQueue(draggingIndex, toIndex);
    }
    setDraggingIndex(null);
  };

  const advanceQueue = async () => {
    const nextIndex = queueIndex + 1;
    if (nextIndex < queue.length) {
      setQueueIndex(nextIndex);
      await axios.post(`${API}/music-queue/${selectedCompetition}/set-index`, { index: nextIndex });
    }
  };

  const addToQueue = (gymnast) => {
    if (queue.some(q => q.id === gymnast.id)) {
      toast.error('Already in order');
      return;
    }
    
    const newGymnast = {
      id: gymnast.id,
      number: gymnast.number,
      letter: gymnast.letter || '',
      firstName: gymnast.preferredFirstName || gymnast.firstName,
      lastName: gymnast.preferredLastName || gymnast.lastName,
      clubName: gymnast.clubName || '',
      floorMusicUrl: gymnast.floorMusicUrl
    };
    
    const newQueue = [...queue, newGymnast];
    setQueue(newQueue);
    saveQueue(newQueue);
    toast.success(`Added ${newGymnast.firstName} to order`);
  };

  const removeFromQueue = (index) => {
    if (index === queueIndex && isPlaying) {
      toast.error("Can't remove while playing");
      return;
    }
    
    const newQueue = queue.filter((_, i) => i !== index);
    
    let newIndex = queueIndex;
    if (index < queueIndex) {
      newIndex = queueIndex - 1;
    } else if (index === queueIndex && queueIndex >= newQueue.length) {
      newIndex = Math.max(0, newQueue.length - 1);
    }
    
    setQueue(newQueue);
    setQueueIndex(newIndex);
    saveQueue(newQueue, newIndex);
  };

  const isInQueue = (gymnastId) => {
    return queue.some(q => q.id === gymnastId);
  };

  // Play the next gymnast in the performance order
  const playNextInQueue = async () => {
    // When currently playing, "next" is queueIndex + 1; when idle, it's queueIndex
    const targetIdx = (isPlaying || isPaused) ? queueIndex + 1 : queueIndex;

    if (targetIdx >= queue.length) {
      toast.info('No more gymnasts in the performance order');
      return;
    }

    const nextGymnast = queue[targetIdx];

    // If something is currently playing/paused, stop it cleanly without
    // calling the full stopFloorMusic (which would also call advanceQueue)
    if ((isPlaying || isPaused) && floorAudioRef.current) {
      markPlayed(pausedGymnastId);
      floorAudioRef.current.pause();
      floorAudioRef.current.currentTime = 0;
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTrack(null);
      // Manually advance to targetIdx
      setQueueIndex(targetIdx);
      try {
        await axios.post(`${API}/music-queue/${selectedCompetition}/set-index`, { index: targetIdx });
      } catch (_) {}
    }

    playFloorMusic(nextGymnast.floorMusicUrl, nextGymnast);
  };

  // Helper to get the proxied audio URL for a gymnast
  const getFloorMusicUrl = (gymnastId) => {
    return `${API}/audio/floor-music/${gymnastId}`;
  };

  // Signal background music to pause
  const signalBackgroundPause = async (reason = 'floor_music') => {
    try {
      console.log('[Floor] Signaling background to PAUSE');
      await axios.post(`${API}/music/signal-pause/${backgroundCompetitionId}`, { reason });
      console.log('[Floor] Pause signal sent successfully');
    } catch (error) {
      console.error('Error signaling background pause:', error);
    }
  };

  // Signal background music to resume
  const signalBackgroundResume = async () => {
    try {
      console.log('[Floor] Signaling background to RESUME');
      await axios.post(`${API}/music/signal-resume/${backgroundCompetitionId}`);
      console.log('[Floor] Resume signal sent successfully');
    } catch (error) {
      console.error('Error signaling background resume:', error);
    }
  };

  // Floor music playback - ALWAYS uses proxy which fetches latest URL from database
  const playFloorMusic = (url, gymnastData) => {
    if (!floorAudioRef.current) {
      toast.error('Audio player not ready');
      return;
    }

    const gymnastName = `${gymnastData.preferredFirstName || gymnastData.firstName} ${gymnastData.preferredLastName || gymnastData.lastName}`;
    
    // Check if this is the same gymnast that was paused - if so, just resume
    if (isPaused && pausedGymnastId === gymnastData.id) {
      floorAudioRef.current.play()
        .then(() => {
          setIsPaused(false);
          setIsPlaying(true);
          toast.success(`Resuming: ${gymnastName}`);
        })
        .catch(error => {
          console.error('Error resuming audio:', error);
          toast.error('Failed to resume playback');
        });
      return;
    }

    // Note: We don't check if url exists here because the proxy endpoint
    // will fetch the CURRENT music URL from the database. This ensures
    // that if a club updates their gymnast's music, the new version plays.

    const proxyUrl = getFloorMusicUrl(gymnastData.id);

    // Signal background music to pause (non-blocking)
    signalBackgroundPause('floor_music');

    // Reset paused state since we're starting fresh
    setIsPaused(false);
    setPausedGymnastId(null);

    // Start playing after small delay for background fade
    setTimeout(() => {
      floorAudioRef.current.src = proxyUrl;
      floorAudioRef.current.volume = floorVolume / 100;
      
      floorAudioRef.current.play()
        .then(() => {
          setCurrentTrack(gymnastName);
          setIsPlaying(true);
          setPlayingType('floor');
          setPausedGymnastId(gymnastData.id); // Track which gymnast is playing
          setShowWordsearch(true); // Show wordsearch during floor music
          toast.success(`Playing: ${gymnastName}`);
          
          axios.post(`${API}/now-playing/start`, {
            competitionId: selectedCompetition,
            gymnast: {
              id: gymnastData.id,
              number: gymnastData.number,
              letter: gymnastData.letter || "",
              name: gymnastName,
              clubName: gymnastData.clubName || ""
            }
          }).catch(err => console.error('Error updating now playing:', err));
        })
        .catch(error => {
          console.error('Error playing audio:', error);
          // Check if it's a 404 (no music found)
          if (error.message?.includes('404') || error.message?.includes('Not Found')) {
            toast.error('No floor music found for this gymnast. The club may need to upload music.');
          } else {
            toast.error(`Failed to play: ${error.message || 'Unknown error'}`);
          }
          setIsPlaying(false);
          setCurrentTrack(null);
          setPlayingType(null);
          setPausedGymnastId(null);
          signalBackgroundResume();
        });
    }, 400);
  };

  const handleFloorMusicEnded = async () => {
    markPlayed(pausedGymnastId); // Grey out this gymnast's tile
    setIsPlaying(false);
    setIsPaused(false); // Reset paused state
    setPausedGymnastId(null); // Clear paused gymnast
    setCurrentTrack(null);
    setPlayingType(null);
    setProgress(0);
    setNowPlayingPreview(null);
    setShowWordsearch(false); // Hide wordsearch when music ends

    try {
      await axios.post(`${API}/now-playing/stop`, {
        competitionId: selectedCompetition
      });
    } catch (error) {
      console.error('Error clearing now playing:', error);
    }

    // Signal background music to resume (will fade back in after 2 sec delay on background page)
    await signalBackgroundResume();

    await advanceQueue();
  };

  const stopFloorMusic = async () => {
    if (floorAudioRef.current) {
      floorAudioRef.current.pause();
      floorAudioRef.current.currentTime = 0;
    }
    markPlayed(pausedGymnastId); // Grey out this gymnast's tile
    setIsPlaying(false);
    setIsPaused(false); // Reset paused state
    setPausedGymnastId(null); // Clear paused gymnast
    setCurrentTrack(null);
    setPlayingType(null);
    setProgress(0);
    setNowPlayingPreview(null);
    setShowWordsearch(false); // Hide wordsearch when stopped
    
    try {
      await axios.post(`${API}/now-playing/stop`, {
        competitionId: selectedCompetition
      });
    } catch (error) {
      console.error('Error clearing now playing:', error);
    }

    // Signal background music to resume
    await signalBackgroundResume();
    
    // Advance queue when manually stopping (gymnast finished)
    await advanceQueue();
  };

  // Timer controls
  const startTimer = async () => {
    if (!selectedCompetition) {
      toast.error('Please select a competition first');
      return;
    }
    
    try {
      announcementsPlayed.current.clear();
      
      await axios.post(`${API}/timer/start`, {
        competitionId: selectedCompetition,
        mode: timerMode,
        duration: timerDuration
      });
      
      setTimerRunning(true);
      setTimerPaused(false);
      toast.success(`${timerMode === 'apparatus' ? 'Apparatus' : 'General'} timer started`);
    } catch (error) {
      console.error('Error starting timer:', error);
      toast.error(`Failed to start timer: ${error.response?.data?.detail || error.message}`);
    }
  };

  const pauseTimer = async () => {
    try {
      await axios.post(`${API}/timer/pause`, {
        competitionId: selectedCompetition
      });
      setTimerPaused(true);
      toast.success('Timer paused');
    } catch (error) {
      console.error('Error pausing timer:', error);
    }
  };

  const resumeTimer = async () => {
    try {
      await axios.post(`${API}/timer/resume`, {
        competitionId: selectedCompetition
      });
      setTimerPaused(false);
      toast.success('Timer resumed');
    } catch (error) {
      console.error('Error resuming timer:', error);
    }
  };

  const stopTimer = async () => {
    try {
      await axios.post(`${API}/timer/stop`, {
        competitionId: selectedCompetition
      });
      setTimerRunning(false);
      setTimerPaused(false);
      toast.success('Timer stopped');
    } catch (error) {
      console.error('Error stopping timer:', error);
    }
  };

  const queueAnnouncement = (timeRemaining, mode) => {
    const url = ANNOUNCEMENTS[mode][timeRemaining];
    
    if (url) {
      console.log(`[Announcement] Queueing announcement for ${timeRemaining}s (mode: ${mode}), URL: ${url}`);
      announcementQueue.current.push(url);
      processAnnouncementQueue();
    } else {
      console.warn(`[Announcement] No URL found for ${timeRemaining}s in mode ${mode}`);
    }
  };

  const processAnnouncementQueue = () => {
    if (isAnnouncementPlaying) {
      console.log('[Announcement] Already playing, waiting...');
      return;
    }
    if (announcementQueue.current.length === 0) return;

    const url = announcementQueue.current.shift();
    console.log(`[Announcement] Processing queue, playing: ${url}`);
    setIsAnnouncementPlaying(true);

    // Signal background music to pause (non-blocking)
    signalBackgroundPause('announcement');

    // Use preloaded audio if available, otherwise create new
    const audioToPlay = preloadedAudio[url] || announcementAudioRef.current;
    
    // Start playing after a small delay for fade
    setTimeout(() => {
      if (preloadedAudio[url]) {
        // Use preloaded audio
        preloadedAudio[url].currentTime = 0;
        preloadedAudio[url].onended = handleAnnouncementEnded;
        preloadedAudio[url].onerror = (e) => {
          console.error('[Announcement] Preloaded audio error:', e);
          setIsAnnouncementPlaying(false);
          signalBackgroundResume();
          setTimeout(() => processAnnouncementQueue(), 500);
        };
        preloadedAudio[url].play()
          .then(() => {
            console.log(`[Announcement] Playing preloaded audio: ${url}`);
            toast.info('Playing warmup announcement', { duration: 2000 });
          })
          .catch(error => {
            console.error('[Announcement] Error playing preloaded:', error);
            setIsAnnouncementPlaying(false);
            signalBackgroundResume();
            setTimeout(() => processAnnouncementQueue(), 500);
          });
      } else {
        // Fallback to regular audio element
        announcementAudioRef.current.src = url;
        announcementAudioRef.current.play()
          .then(() => {
            console.log(`[Announcement] Playing via ref: ${url}`);
            toast.info('Playing warmup announcement', { duration: 2000 });
          })
          .catch(error => {
            console.error('[Announcement] Error playing via ref:', error);
            setIsAnnouncementPlaying(false);
            signalBackgroundResume();
            setTimeout(() => processAnnouncementQueue(), 500);
          });
      }
    }, 400);
  };

  const handleAnnouncementEnded = () => {
    console.log('[Announcement] Ended');
    setIsAnnouncementPlaying(false);
    
    // Resume background immediately for announcements
    if (announcementQueue.current.length === 0) {
      signalBackgroundResume();
    }
    
    // Process next announcement after short delay
    setTimeout(() => processAnnouncementQueue(), 500);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!pinEntered) return;

      switch (e.key) {
        case 'Escape':
          if (isPlaying && floorAudioRef.current) {
            floorAudioRef.current.pause();
            handleFloorMusicEnded();
          }
          break;
        case 't':
        case 'T':
          if (timerRunning) {
            if (timerPaused) resumeTimer();
            else pauseTimer();
          } else {
            startTimer();
          }
          break;
        case 'p':
        case 'P':
          if (isPlaying && floorAudioRef.current) {
            floorAudioRef.current.pause();
            handleFloorMusicEnded();
          }
          break;
        case 'r':
        case 'R':
          if (timerRunning) stopTimer();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pinEntered, isPlaying, timerRunning, timerPaused]);

  if (!pinEntered) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-gray-200">
          <CardHeader className="text-center">
            <img src="/scorz-logo.png" alt="Scorz Logo" className="h-16 mx-auto mb-4" />
            <CardTitle className="text-2xl font-bold text-black">
              Floor Music Control
            </CardTitle>
            <p className="text-gray-500 text-sm mt-2">
              Enter PIN to access
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <Input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter PIN"
                className="text-center text-2xl tracking-widest"
                maxLength={4}
                autoFocus
                data-testid="floor-music-pin-input"
              />
              <Button type="submit" className="w-full bg-black hover:bg-gray-800" data-testid="floor-music-submit-btn">
                Access Floor Music
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="link" onClick={() => navigate('/music/background')} className="text-gray-500">
                Go to Background Music
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 p-3 overflow-hidden flex flex-col relative">
      <div className="flex-1 flex flex-col gap-3 min-h-0">
        {/* Header Row */}
        <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
          <Button variant="ghost" size="sm" onClick={() => navigate('/music/background')} className="shrink-0">
            <ArrowLeft className="w-4 h-4 mr-1" /> Background
          </Button>
          <img src="/scorz-logo.png" alt="Scorz" className="h-8 shrink-0" />
          
          {/* Selectors */}
          <div className="flex items-center gap-2">
            <Select value={selectedCompetition} onValueChange={(val) => {
              setSelectedCompetition(val);
              setSelectedRound('');
              setSelectedRotation('');
              setRotationStarted(false);
              setGymnasts([]);
              setFloorGroups([]);
              loadTrainingVideoSettings(val);
            }}>
              <SelectTrigger className="w-[160px]" data-testid="floor-competition-select">
                <SelectValue placeholder="Competition" />
              </SelectTrigger>
              <SelectContent>
                {competitions.map(comp => (
                  <SelectItem key={comp.id} value={comp.id}>{comp.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedRound} onValueChange={(val) => {
              setSelectedRound(val);
              setSelectedRotation('');
              setRotationStarted(false);
              setGymnasts([]);
              setFloorGroups([]);
            }} disabled={!selectedCompetition}>
              <SelectTrigger className="w-[140px]" data-testid="floor-round-select">
                <SelectValue placeholder="Round" />
              </SelectTrigger>
              <SelectContent>
                {programmes.map(round => (
                  <SelectItem key={round.roundNumber} value={round.roundNumber.toString()}>
                    {round.roundName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedRotation} onValueChange={(val) => {
              setSelectedRotation(val);
              setRotationStarted(false);
              setGymnasts([]);
            }} disabled={!selectedRound}>
              <SelectTrigger className="w-[140px]" data-testid="floor-rotation-select">
                <SelectValue placeholder="Rotation" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map(num => (
                  <SelectItem key={num} value={num.toString()}>
                    Rotation {num}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Training Video Broadcast Button */}
          {selectedCompetition && trainingVideoSettings?.videoUrl && (
            <div className="flex items-center">
              {trainingVideoPlaying ? (
                <Button
                  onClick={stopTrainingVideo}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="stop-training-video-btn"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Stop Judge Video
                </Button>
              ) : (
                <Button
                  onClick={broadcastTrainingVideo}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="play-training-video-btn"
                >
                  <Video className="w-4 h-4 mr-1" />
                  Play Judge Video
                </Button>
              )}
            </div>
          )}
          
          {/* Timer Display in Header */}
          <div data-testid="header-timer" className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1">
            <span className="text-xs text-gray-500 uppercase font-medium">
              {timerMode === 'general' ? 'Gen' : 'App'}
            </span>
            <div className={`text-2xl font-bold tabular-nums px-2 py-0.5 rounded min-w-[70px] text-center ${
              timerRunning && previewTime <= 30 && previewTime > 0 ? 'bg-red-500 text-white' : 
              timerRunning && previewTime <= 60 && previewTime > 30 ? 'bg-yellow-500 text-white' : 
              timerRunning ? 'bg-green-500 text-white' :
              'bg-white text-gray-800'
            }`}>
              {timerRunning 
                ? `${Math.floor(previewTime / 60)}:${(previewTime % 60).toString().padStart(2, '0')}`
                : `${Math.floor(timerDuration / 60)}:${(timerDuration % 60).toString().padStart(2, '0')}`
              }
            </div>
          </div>

          {floorGroups.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-100 rounded-lg">
                <span className="text-sm text-green-800 font-medium">Floor:</span>
                {floorGroups.map(group => (
                  <span key={group} className="text-sm font-semibold text-green-700 bg-green-200 px-2 py-0.5 rounded">
                    {group}
                  </span>
                ))}
              </div>
            )}
          
          {/* Spacer to push stop button right */}
          <div className="flex-1" />
          
          {rotationStarted && (
            <Button 
              onClick={async () => { 
                // Get current rotation number before clearing
                const currentRotNum = parseInt(selectedRotation);
                const nextRotNum = currentRotNum + 1;
                const maxRotations = 5; // Typically 5 rotations max
                
                // Clear current state
                setRotationStarted(false); 
                setGymnasts([]); 
                setFloorGroups([]);
                setPlayedGymnastIds(new Set()); // Reset played tracking for new rotation
                
                // Clear the programme order (queue)
                setQueue([]);
                setQueueIndex(0);
                try {
                  await axios.put(`${API}/music-queue/${selectedCompetition}`, {
                    gymnasts: [],
                    currentIndex: 0
                  });
                } catch (error) {
                  console.error('Error clearing queue:', error);
                }
                
                // Auto-advance to next rotation if not at max
                if (nextRotNum <= maxRotations) {
                  setSelectedRotation(nextRotNum.toString());
                  toast.info(`Moving to Rotation ${nextRotNum}`);
                  // Sync floor state with new rotation (will re-sync once gymnasts load)
                  axios.put(`${API}/floor-state`, {
                    competitionId: selectedCompetition,
                    competitionTitle: competitions.find(c => c.id === selectedCompetition)?.title || '',
                    roundNumber: parseInt(selectedRound),
                    rotationNumber: nextRotNum,
                    isActive: false  // inactive until gymnasts load for new rotation
                  }).catch(() => {});
                } else {
                  setSelectedRotation('');
                  axios.put(`${API}/floor-state`, { competitionId: selectedCompetition, isActive: false }).catch(() => {});
                  toast.success('All rotations complete!');
                }
              }} 
              variant="destructive"
              size="sm"
              data-testid="floor-stop-btn"
            >
              <Square className="w-4 h-4 mr-1" /> Stop
            </Button>
          )}
          
          {/* Fullscreen Button */}
          <FullscreenButton />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex gap-3 min-h-0 overflow-hidden">
          {/* Gymnasts Grid */}
          <div className="flex-1 bg-white rounded-lg shadow-sm p-3 overflow-auto relative">
            {/* Wordsearch popup - positioned over gymnast grid only */}
            {wordsearchEnabled && wordsearchWords.length > 0 && showWordsearch && playingType === 'floor' && (
              <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                <Wordsearch words={wordsearchWords} isVisible={true} />
              </div>
            )}
            
            {!rotationStarted ? (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Music className="w-16 h-16 mx-auto mb-3 opacity-50" />
                  <p className="text-lg">Select competition, round & rotation</p>
                  <p className="text-sm mt-2">Groups on Floor will be loaded automatically</p>
                </div>
              </div>
            ) : loadingGymnasts ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-lg text-gray-500">Loading gymnasts...</div>
              </div>
            ) : gymnasts.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No gymnasts with floor music in {floorGroups.join(', ') || 'selected groups'}</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-2">
                {(() => {
                  // Sort: queue order if set, otherwise programme number order
                  let sorted;
                  if (queue.length > 0) {
                    const queueIds = queue.map(q => q.id);
                    const inQueueOrder = queue
                      .map(q => gymnasts.find(g => g.id === q.id))
                      .filter(Boolean);
                    const notInQueue = gymnasts
                      .filter(g => !queueIds.includes(g.id))
                      .sort((a, b) => {
                        const na = parseInt(a.number) || 0, nb = parseInt(b.number) || 0;
                        if (na !== nb) return na - nb;
                        return (a.letter || '').localeCompare(b.letter || '');
                      });
                    sorted = [...inQueueOrder, ...notInQueue];
                  } else {
                    sorted = [...gymnasts].sort((a, b) => {
                      const na = parseInt(a.number) || 0, nb = parseInt(b.number) || 0;
                      if (na !== nb) return na - nb;
                      return (a.letter || '').localeCompare(b.letter || '');
                    });
                  }
                  return sorted.map(gymnast => {
                    const isCurrentlyPlaying = pausedGymnastId === gymnast.id && (isPlaying || isPaused);
                    const inQueue = isInQueue(gymnast.id);
                    const isPlayed = playedGymnastIds.has(gymnast.id);
                    return (
                      <div 
                        key={gymnast.id} 
                        className="relative cursor-pointer"
                        onClick={() => playFloorMusic(gymnast.floorMusicUrl, gymnast)}
                        data-testid={`gymnast-container-${gymnast.number}`}
                      >
                        <div
                          className={`h-24 w-full flex flex-col items-center justify-center p-1 transition-all rounded-md ${
                            isCurrentlyPlaying 
                              ? 'bg-green-600 hover:bg-green-700 text-white ring-4 ring-green-300' 
                              : isPlayed
                              ? 'bg-gray-50 hover:bg-gray-100 text-gray-400 border border-gray-200'
                              : inQueue
                              ? 'bg-blue-50 hover:bg-blue-100 text-gray-900 border-2 border-blue-400'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'
                          } ${isPlayed && !isCurrentlyPlaying ? 'opacity-50' : ''}`}
                          data-testid={`gymnast-btn-${gymnast.number}`}
                        >
                          <div className={`font-bold text-xl ${isCurrentlyPlaying ? 'text-white' : isPlayed ? 'text-gray-400' : 'text-black'}`}>
                            {gymnast.number}{gymnast.letter}
                          </div>
                          <div className={`text-xs truncate w-full text-center ${isCurrentlyPlaying ? 'text-white' : isPlayed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                            {gymnast.preferredFirstName}
                          </div>
                          <div className={`text-xs truncate w-full text-center ${isCurrentlyPlaying ? 'text-green-100' : isPlayed ? 'text-gray-300' : 'text-gray-500'}`}>
                            {gymnast.preferredLastName}
                          </div>
                          {isPlayed && !isCurrentlyPlaying && (
                            <div className="text-[9px] text-gray-400 mt-0.5">played</div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (inQueue) {
                              const idx = queue.findIndex(q => q.id === gymnast.id);
                              if (idx !== -1) removeFromQueue(idx);
                            } else {
                              addToQueue(gymnast);
                            }
                          }}
                          className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs shadow-md transition-colors ${
                            inQueue 
                              ? 'bg-red-500 hover:bg-red-600' 
                              : 'bg-blue-500 hover:bg-blue-600'
                          }`}
                          title={inQueue ? 'Remove from order' : 'Add to order'}
                        >
                          {inQueue ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                        </button>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="w-72 flex flex-col gap-3">
            {/* Now Playing */}
            <div className={`rounded-lg p-4 shadow-sm ${isPlaying ? 'bg-green-600 text-white' : 'bg-white'}`}>
              <div className="text-xs font-semibold uppercase opacity-75 mb-2">
                {isPlaying ? 'Now Playing' : 'Ready'}
              </div>
              
              {/* Show ceremony/march/podium track name when playing */}
              {isPlaying && playingType === 'ceremony' ? (
                <div className="text-center">
                  <div className="text-4xl font-bold">
                    <Music className="w-10 h-10 mx-auto mb-2" />
                  </div>
                  <div className="text-lg font-semibold mt-1">{currentTrack}</div>
                  <div className="text-sm opacity-75">Ceremony Music</div>
                </div>
              ) : nowPlayingPreview ? (
                <div className="text-center">
                  <div className="text-4xl font-bold">
                    {nowPlayingPreview.gymnastNumber}{nowPlayingPreview.gymnastLetter}
                  </div>
                  <div className="text-lg font-semibold mt-1">{nowPlayingPreview.gymnastName}</div>
                  <div className="text-sm opacity-75">{nowPlayingPreview.clubName}</div>
                  {isPaused && (
                    <div className="text-xs mt-2 text-yellow-300 font-semibold animate-pulse">
                      ⏸ PAUSED - Click play to resume
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-400 py-4">
                  <Music className="w-8 h-8 mx-auto mb-1 opacity-50" />
                  <p className="text-sm">Click gymnast to play</p>
                </div>
              )}
              
              {/* Pause/Resume/Stop buttons - same for both floor and ceremony music */}
              {isPlaying && (
                <div className="mt-3 flex gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    className="flex-1"
                    onClick={() => {
                      if (playingType === 'ceremony') {
                        ceremonyAudioRef.current?.pause();
                      } else {
                        if (isPaused) {
                          // Resume playback
                          floorAudioRef.current?.play();
                          setIsPaused(false);
                        } else {
                          // Pause playback
                          floorAudioRef.current?.pause();
                          setIsPaused(true);
                        }
                      }
                    }}
                  >
                    {isPaused && playingType === 'floor' ? (
                      <Play className="w-4 h-4" />
                    ) : (
                      <Pause className="w-4 h-4" />
                    )}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    className="flex-1"
                    onClick={() => {
                      if (playingType === 'ceremony') {
                        stopCeremonyMusic();
                      } else {
                        stopFloorMusic();
                      }
                    }}
                  >
                    <Square className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              {/* Music Progress Bar */}
              {isPlaying && duration > 0 && (
                <div className="mt-3">
                  <div className="w-full bg-white/30 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-white h-full rounded-full transition-all duration-200"
                      style={{ width: `${(progress / duration) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>{Math.floor(progress / 60)}:{Math.floor(progress % 60).toString().padStart(2, '0')}</span>
                    <span>{Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, '0')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Compact Timer Controls */}
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    variant={timerMode === 'general' ? 'default' : 'outline'}
                    className={`text-xs px-2 h-6 ${timerMode === 'general' ? 'bg-black' : ''}`}
                    onClick={() => setTimerMode('general')}
                  >
                    Gen
                  </Button>
                  <Button 
                    size="sm" 
                    variant={timerMode === 'apparatus' ? 'default' : 'outline'}
                    className={`text-xs px-2 h-6 ${timerMode === 'apparatus' ? 'bg-black' : ''}`}
                    onClick={() => setTimerMode('apparatus')}
                  >
                    App
                  </Button>
                </div>
                <span className="text-lg font-bold tabular-nums">
                  {Math.floor(timerDuration / 60)}:{(timerDuration % 60).toString().padStart(2, '0')}
                </span>
              </div>
              
              <div className="grid grid-cols-4 gap-1 mb-2">
                <Button size="sm" variant="outline" className="text-xs h-7 text-red-600" onClick={() => setTimerDuration(prev => Math.max(0, prev - 60))} disabled={timerRunning}>-1m</Button>
                <Button size="sm" variant="outline" className="text-xs h-7 text-red-600" onClick={() => setTimerDuration(prev => Math.max(0, prev - 30))} disabled={timerRunning}>-30s</Button>
                <Button size="sm" variant="outline" className="text-xs h-7 text-green-600" onClick={() => setTimerDuration(prev => prev + 30)} disabled={timerRunning}>+30s</Button>
                <Button size="sm" variant="outline" className="text-xs h-7 text-green-600" onClick={() => setTimerDuration(prev => prev + 60)} disabled={timerRunning}>+1m</Button>
              </div>
              
              <div className="flex gap-1">
                {!timerRunning ? (
                  <Button onClick={startTimer} size="sm" className="flex-1 h-8 bg-green-600 hover:bg-green-700">
                    <Play className="w-4 h-4 mr-1" /> Start
                  </Button>
                ) : (
                  <>
                    <Button onClick={timerPaused ? resumeTimer : pauseTimer} size="sm" className="flex-1 h-8 bg-yellow-500 hover:bg-yellow-600">
                      {timerPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    </Button>
                    <Button onClick={stopTimer} size="sm" variant="destructive" className="flex-1 h-8">
                      <Square className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Performance Order Queue */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden flex-1 flex flex-col min-h-[300px]">
              <div className="flex items-center justify-between p-2 border-b bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <ListOrdered className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-semibold uppercase text-gray-600">Performance Order</span>
                </div>
                <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">{queue.length}</span>
              </div>
              
              {queue.length > 0 ? (
                <div className="overflow-y-auto flex-1">
                  {queue.map((gymnast, index) => {
                    const isCurrent = index === queueIndex;
                    const isNext = index === queueIndex + 1;
                    const isPast = index < queueIndex;
                    
                    return (
                      <div
                        key={`${gymnast.id}-${index}`}
                        draggable={!isPlaying || index !== queueIndex}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`flex items-center gap-1 py-1 px-1.5 border-b cursor-move transition-colors ${
                          isCurrent ? 'bg-green-100 border-l-4 border-l-green-500' :
                          isNext ? 'bg-blue-50 border-l-4 border-l-blue-400' :
                          isPast ? 'bg-gray-100 opacity-50' :
                          'hover:bg-gray-50 border-l-4 border-l-transparent'
                        } ${draggingIndex === index ? 'opacity-50' : ''}`}
                      >
                        <GripVertical className="w-3 h-3 text-gray-300 flex-shrink-0" />
                        <span className="text-[10px] font-mono text-gray-400 w-3">{index + 1}</span>
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <span className="font-bold text-xs">{gymnast.number}{gymnast.letter}</span>
                          <span className="text-xs truncate">{gymnast.firstName}</span>
                        </div>
                        {isCurrent && (
                          <span className="text-[9px] bg-green-500 text-white px-1 rounded flex-shrink-0">NOW</span>
                        )}
                        {isNext && (
                          <span className="text-[9px] bg-blue-500 text-white px-1 rounded flex-shrink-0">NEXT</span>
                        )}
                        {!isCurrent && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromQueue(index);
                            }}
                            className="p-0.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-500 flex-shrink-0"
                            title="Remove from order"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center p-4">
                  <div className="text-center text-gray-400">
                    <ListOrdered className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-xs">Click + on gymnasts to add to order</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Play Next Button — only shown when a performance order exists */}
        {queue.length > 0 && (() => {
          const targetIdx = (isPlaying || isPaused) ? queueIndex + 1 : queueIndex;
          const nextGymnast = targetIdx < queue.length ? queue[targetIdx] : null;
          return (
            <div className="flex justify-center py-2 shrink-0">
              <button
                data-testid="play-next-btn"
                onClick={playNextInQueue}
                disabled={!nextGymnast}
                className={`flex items-center gap-3 px-8 py-3 rounded-xl font-bold text-white shadow-lg transition-all ${
                  nextGymnast
                    ? 'bg-green-600 hover:bg-green-500 active:scale-95 cursor-pointer'
                    : 'bg-gray-300 cursor-not-allowed opacity-60'
                }`}
              >
                <Play className="w-5 h-5 fill-white" />
                <span className="text-base">
                  {nextGymnast
                    ? `Play Next — #${nextGymnast.number}${nextGymnast.letter || ''} ${nextGymnast.firstName} ${nextGymnast.lastName}`
                    : 'Performance Order Complete'}
                </span>
              </button>
            </div>
          );
        })()}

        {/* March/Ceremony Music Footer */}
        <div className="bg-white rounded-lg shadow-sm p-2 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-semibold uppercase text-purple-700">March / Podium</span>
              <button
                onClick={() => setShowCeremonySettings(true)}
                className="p-1 hover:bg-purple-100 rounded text-purple-600"
                title="Manage tracks"
              >
                <Settings className="w-3 h-3" />
              </button>
            </div>
            
            <div className="flex-1 flex items-center gap-2 overflow-x-auto">
              {/* March tracks */}
              {ceremonyTracks.filter(t => t.type === 'march').map(track => (
                <Button
                  key={track.id}
                  size="sm"
                  variant={currentTrack === track.name ? 'default' : 'outline'}
                  className={`text-xs h-7 whitespace-nowrap ${currentTrack === track.name ? 'bg-purple-600' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`}
                  onClick={() => playCeremonyMusic(track)}
                >
                  <Users className="w-3 h-3 mr-1" />
                  {track.name}
                </Button>
              ))}
              
              {/* Podium tracks */}
              {ceremonyTracks.filter(t => t.type === 'podium').map(track => (
                <Button
                  key={track.id}
                  size="sm"
                  variant={currentTrack === track.name ? 'default' : 'outline'}
                  className={`text-xs h-7 whitespace-nowrap ${currentTrack === track.name ? 'bg-purple-600' : 'border-yellow-400 text-yellow-700 hover:bg-yellow-50'}`}
                  onClick={() => playCeremonyMusic(track)}
                >
                  <Award className="w-3 h-3 mr-1" />
                  {track.name}
                </Button>
              ))}
              
              {ceremonyTracks.length === 0 && (
                <button 
                  onClick={() => setShowCeremonySettings(true)}
                  className="text-xs text-purple-600 hover:underline"
                >
                  Click to add march/podium music
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden audio elements */}
      <audio
        ref={floorAudioRef}
        onEnded={handleFloorMusicEnded}
        onTimeUpdate={(e) => {
          setProgress(e.target.currentTime);
          setDuration(e.target.duration || 0);
        }}
        onError={(e) => {
          console.error('Floor music error:', e);
          toast.error('Failed to load floor music');
          setIsPlaying(false);
          setCurrentTrack(null);
          signalBackgroundResume();
        }}
        preload="auto"
      />
      <audio
        ref={announcementAudioRef}
        onEnded={handleAnnouncementEnded}
        onError={(e) => {
          console.error('Announcement error:', e);
          setIsAnnouncementPlaying(false);
          signalBackgroundResume();
          setTimeout(() => processAnnouncementQueue(), 500);
        }}
        preload="auto"
      />
      <audio
        ref={ceremonyAudioRef}
        onEnded={handleCeremonyMusicEnded}
        onError={(e) => {
          console.error('Ceremony music error:', e);
          toast.error('Failed to load ceremony music');
          setIsPlaying(false);
          setCurrentTrack(null);
          signalBackgroundResume();
        }}
        preload="auto"
      />

      {/* Ceremony Music Settings Modal */}
      {showCeremonySettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold">March / Podium Music</h2>
              <button 
                onClick={() => setShowCeremonySettings(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Upload Section */}
            <div className="p-4 border-b bg-gray-50">
              <div className="text-sm font-semibold mb-3">Upload New Track</div>
              <div className="space-y-3">
                <Input
                  placeholder="Track name (e.g., March In, Simply The Best)"
                  value={newTrackName}
                  onChange={(e) => setNewTrackName(e.target.value)}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Select value={newTrackType} onValueChange={setNewTrackType}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="march">March</SelectItem>
                      <SelectItem value="podium">Podium</SelectItem>
                    </SelectContent>
                  </Select>
                  <input
                    ref={ceremonyFileRef}
                    type="file"
                    accept=".mp3,audio/mpeg"
                    className="flex-1 text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-purple-100 file:text-purple-700 file:text-sm file:cursor-pointer"
                  />
                </div>
                <Button 
                  onClick={uploadCeremonyTrack}
                  disabled={uploadingCeremony}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingCeremony ? 'Uploading...' : 'Upload Track'}
                </Button>
              </div>
            </div>
            
            {/* Track List */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* March Tracks */}
              <div className="mb-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Users className="w-4 h-4" /> March Tracks
                </div>
                {ceremonyTracks.filter(t => t.type === 'march').length > 0 ? (
                  <div className="space-y-1">
                    {ceremonyTracks.filter(t => t.type === 'march').map(track => (
                      <div key={track.id} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100">
                        <div className="flex items-center gap-2">
                          <Music className="w-4 h-4 text-purple-500" />
                          <span className="text-sm">{track.name}</span>
                        </div>
                        <button
                          onClick={() => deleteCeremonyTrack(track.id, track.name)}
                          className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No march tracks uploaded</p>
                )}
              </div>
              
              {/* Podium Tracks */}
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Award className="w-4 h-4" /> Podium Tracks
                </div>
                {ceremonyTracks.filter(t => t.type === 'podium').length > 0 ? (
                  <div className="space-y-1">
                    {ceremonyTracks.filter(t => t.type === 'podium').map(track => (
                      <div key={track.id} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100">
                        <div className="flex items-center gap-2">
                          <Music className="w-4 h-4 text-purple-500" />
                          <span className="text-sm">{track.name}</span>
                        </div>
                        <button
                          onClick={() => deleteCeremonyTrack(track.id, track.name)}
                          className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No podium tracks uploaded</p>
                )}
              </div>
            </div>
            
            <div className="p-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowCeremonySettings(false)}
                className="w-full"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
